import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type AiLookup, type AiNewModel, type AiProviderModels, aiApi, type Int, existingModelId, modelIdFor, num, usdToMicros } from '@/lib/aiAdmin';

import { errorText, useLoad } from './common';

const SUPPORTS = [
  ['supportsTools', 'Tools'],
  ['supportsVision', 'Images'],
  ['supportsReasoning', 'Reasoning'],
  ['supportsPromptCaching', 'Prompt caching'],
] as const;
type Supports = Record<(typeof SUPPORTS)[number][0], boolean>;

/** Micro-USD per 1M tokens as the USD text an admin types: "1.25". */
const usd = (micros: Int | undefined): string => (micros === undefined ? '' : String(Number((num(micros) / 1e6).toFixed(6))));
const SEARCH_DELAY_MS = 200;
const whole = (text: string): number | undefined => (/^[1-9]\d{0,9}$/.test(text.trim()) ? Number(text) : undefined);

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

/** Adds a model OFF: pick a provider and a model name, look up its prices and limits, edit them, save. */
export function AddModelDialog({ modelIds, onClose, onAdded }: { modelIds: string[]; onClose: () => void; onAdded: () => void }) {
  const providers = useLoad(async () => (await aiApi.addableProviders()).providers ?? [], []);
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [lookup, setLookup] = useState<AiLookup | undefined>();
  const [looking, setLooking] = useState(false);
  const [prices, setPrices] = useState({ input: '', output: '', cacheRead: '', cacheWrite: '' });
  const [context, setContext] = useState('');
  const [maxOutput, setMaxOutput] = useState('');
  const [supports, setSupports] = useState<Supports>({ supportsTools: false, supportsVision: false, supportsReasoning: false, supportsPromptCaching: false });
  const [modelId, setModelId] = useState<string | undefined>();
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState<AiProviderModels | undefined>();
  const [matchError, setMatchError] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const name = model.trim();
  const id = modelId ?? modelIdFor(provider, name);
  const existing = existingModelId(id, modelIds);
  const changeName = (value: string) => {
    setModel(value);
    setLookup(undefined);
    setListOpen(true);
    setActive(-1);
  };
  const changeProvider = (value: string) => {
    setProvider(value);
    setLookup(undefined);
    setMatches(undefined);
    setMatchError(false);
    setListOpen(false);
    setActive(-1);
  };

  // While the list is open, ask for the matches 200 ms after the last key. The cleanup drops an answer that arrives late.
  useEffect(() => {
    if (!listOpen || !provider) return undefined;
    let stale = false;
    const timer = setTimeout(() => {
      aiApi
        .providerModels(provider, name)
        .then((found) => {
          if (stale) return;
          setMatches(found);
          setMatchError(false);
        })
        .catch(() => {
          if (!stale) setMatchError(true);
        });
    }, SEARCH_DELAY_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [listOpen, provider, name]);

  const shown = matches?.models ?? [];
  const pick = (picked: string) => {
    setModel(picked);
    setListOpen(false);
    setActive(-1);
    void find(picked);
  };
  const onNameKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && listOpen) {
      // Closes the list only: the dialog listens for Escape on the document.
      event.preventDefault();
      event.stopPropagation();
      setListOpen(false);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!listOpen) return setListOpen(true);
      if (shown.length) setActive((active + (event.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length);
    } else if (event.key === 'Enter' && listOpen && shown[active]) {
      event.preventDefault();
      pick(shown[active]!);
    }
  };

  const find = async (modelName = name) => {
    setLooking(true);
    try {
      const found = await aiApi.lookupModel(provider, modelName);
      setLookup(found);
      if (found.found) {
        setPrices({ input: usd(found.inputMicrosPerMtok), output: usd(found.outputMicrosPerMtok), cacheRead: usd(found.cacheReadMicrosPerMtok), cacheWrite: usd(found.cacheWriteMicrosPerMtok) });
        setContext(found.contextWindow ? String(found.contextWindow) : '');
        setMaxOutput(found.maxOutputTokens ? String(found.maxOutputTokens) : '');
        setSupports({
          supportsTools: found.supportsTools === true,
          supportsVision: found.supportsVision === true,
          supportsReasoning: found.supportsReasoning === true,
          supportsPromptCaching: found.supportsPromptCaching === true,
        });
      }
    } catch (error) {
      toast.error(errorText(error, 'The look up failed'));
    }
    setLooking(false);
  };

  const save = async () => {
    const input = usdToMicros(prices.input);
    const output = usdToMicros(prices.output);
    if (!provider || !name || !id) return void window.alert('Choose a provider and enter a model name.');
    if (input === undefined || output === undefined) return void window.alert('The input and output prices are required, as USD per 1M tokens.');
    const cacheRead = prices.cacheRead.trim() ? usdToMicros(prices.cacheRead) : undefined;
    const cacheWrite = prices.cacheWrite.trim() ? usdToMicros(prices.cacheWrite) : undefined;
    if ((prices.cacheRead.trim() && cacheRead === undefined) || (prices.cacheWrite.trim() && cacheWrite === undefined)) return void window.alert('A cache price must be USD per 1M tokens.');
    const contextWindow = context.trim() ? whole(context) : undefined;
    const maxOutputTokens = maxOutput.trim() ? whole(maxOutput) : undefined;
    if ((context.trim() && !contextWindow) || (maxOutput.trim() && !maxOutputTokens)) return void window.alert('A limit must be a whole number above 0.');
    const body: AiNewModel = {
      provider,
      providerModel: name,
      modelId: existing ?? id,
      ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      prices: { inputMicrosPerMtok: input, outputMicrosPerMtok: output, ...(cacheRead ? { cacheReadMicrosPerMtok: cacheRead } : {}), ...(cacheWrite ? { cacheWriteMicrosPerMtok: cacheWrite } : {}) },
      ...(contextWindow ? { contextWindow } : {}),
      ...(maxOutputTokens ? { maxOutputTokens } : {}),
      ...supports,
    };
    setSaving(true);
    try {
      const added = await aiApi.addModel(body);
      toast.success(added.created ? `${added.modelId} is added and off` : `${added.modelId}: route added, off`);
      onAdded();
    } catch (error) {
      toast.error(errorText(error, 'The model was not added'));
    }
    setSaving(false);
  };

  const price = (key: keyof typeof prices, label: string) => (
    <Field id={`add-model-price-${key}`} label={label}>
      <Input id={`add-model-price-${key}`} inputMode="decimal" value={prices[key]} onChange={(event) => setPrices({ ...prices, [key]: event.target.value })} className="h-9" />
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add model"
      description="The model or route is added off. Test it, then turn it on."
      className="max-w-xl"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} disabled={saving || !provider || !name}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {providers.error ? <p className="text-sm text-destructive">{providers.error}</p> : null}
        <Field id="add-model-provider" label="Provider">
          {providers.data?.length === 0 && !providers.error ? (
            <p id="add-model-provider" className="text-sm text-muted-foreground">
              No provider can be added here
            </p>
          ) : (
            // A native select: its menu cannot hide behind the dialog.
            <select
              id="add-model-provider"
              value={provider}
              onChange={(event) => changeProvider(event.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{providers.loading ? 'Loading' : 'Choose a provider'}</option>
              {(providers.data ?? []).map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field id="add-model-name" label="Model name at the provider">
          <div className="flex gap-2">
            <Input
              id="add-model-name"
              role="combobox"
              aria-expanded={listOpen}
              aria-controls="add-model-matches"
              aria-autocomplete="list"
              aria-activedescendant={active >= 0 ? `add-model-match-${active}` : undefined}
              autoComplete="off"
              value={model}
              onChange={(event) => changeName(event.target.value)}
              onFocus={() => setListOpen(true)}
              onBlur={() => setListOpen(false)}
              onKeyDown={onNameKey}
              placeholder="gemini-2.5-flash"
              className="h-9"
            />
            <Button type="button" variant="outline" className="h-9" disabled={looking || !provider || !name} onClick={() => void find()}>
              Look up
            </Button>
          </div>
          {listOpen && provider ? (
            <div className="rounded-md border border-border">
              {shown.length ? (
                <ul id="add-model-matches" role="listbox" className="max-h-48 overflow-y-auto py-1">
                  {shown.map((one, index) => (
                    <li
                      key={one}
                      id={`add-model-match-${index}`}
                      role="option"
                      aria-selected={index === active}
                      // mouse down, not click: the input must keep focus, or its blur closes the list first
                      onMouseDown={(event) => {
                        event.preventDefault();
                        pick(one);
                      }}
                      className={`cursor-pointer px-3 py-1.5 text-sm ${index === active ? 'bg-muted' : 'hover:bg-muted'}`}
                    >
                      {one}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-2 text-sm text-muted-foreground">
                  {matchError ? 'The search failed. You can still type the name.' : matches ? 'No match. You can still type the name.' : 'Searching'}
                </p>
              )}
              {matches?.total !== undefined && matches.total > shown.length ? (
                <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">{`${shown.length} of ${matches.total}`}</p>
              ) : null}
            </div>
          ) : null}
        </Field>
        {lookup?.found === false ? <p className="text-sm text-muted-foreground">LiteLLM does not know this model. Enter the prices.</p> : null}
        {lookup?.found && lookup.tieredPricing ? <p className="text-sm text-muted-foreground">This model has prices by token tier. These are the base prices.</p> : null}
        <div className="grid grid-cols-2 gap-3">
          {price('input', 'Input, USD per 1M tokens')}
          {price('output', 'Output, USD per 1M tokens')}
          {price('cacheRead', 'Cache read, USD per 1M')}
          {price('cacheWrite', 'Cache write, USD per 1M')}
          <Field id="add-model-context" label="Context window">
            <Input id="add-model-context" inputMode="numeric" value={context} onChange={(event) => setContext(event.target.value)} className="h-9" />
          </Field>
          <Field id="add-model-max-output" label="Max output tokens">
            <Input id="add-model-max-output" inputMode="numeric" value={maxOutput} onChange={(event) => setMaxOutput(event.target.value)} className="h-9" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-4">
          {SUPPORTS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={supports[key]} onChange={(event) => setSupports({ ...supports, [key]: event.target.checked })} />
              {label}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field id="add-model-id" label="Model id">
            <Input id="add-model-id" value={id} onChange={(event) => setModelId(event.target.value)} className="h-9" />
            {existing ? <p className="text-xs text-muted-foreground">{`This adds a route to ${existing}. Its name and limits stay as they are.`}</p> : null}
          </Field>
          <Field id="add-model-display" label="Display name">
            <Input id="add-model-display" value={displayName} onChange={(event) => setDisplayName(event.target.value)} className="h-9" />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
